import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { MessageCircle, Users, Clock, AlertCircle, Mail, User } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ConversationsDashboard() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [credentials, setCredentials] = useState({ username: "", password: "" });
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [chatHistory, setChatHistory] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const authToken = localStorage.getItem('admin_token');

  useEffect(() => {
    if (authToken) {
      setIsAuthenticated(true);
      loadConversations();
    }
  }, [authToken]);

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/admin/login`, credentials);
      if (response.data.success) {
        localStorage.setItem('admin_token', response.data.token);
        setIsAuthenticated(true);
        toast.success("تم تسجيل الدخول بنجاح");
        loadConversations();
      } else {
        toast.error("اسم المستخدم أو كلمة المرور غير صحيحة");
      }
    } catch (error) {
      console.error("Login error:", error);
      toast.error("حدث خطأ في تسجيل الدخول");
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    setIsAuthenticated(false);
    toast.success("تم تسجيل الخروج");
  };

  const loadConversations = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/admin/conversations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConversations(response.data);
    } catch (error) {
      console.error("Error loading conversations:", error);
      toast.error("حدث خطأ في تحميل المحادثات");
    } finally {
      setLoading(false);
    }
  };

  const viewConversation = async (conversation) => {
    try {
      const response = await axios.get(`${API}/chat/${conversation.customer_id}`);
      setChatHistory(response.data);
      setSelectedConversation(conversation);
      setIsDialogOpen(true);
    } catch (error) {
      console.error("Error loading chat history:", error);
      toast.error("حدث خطأ في تحميل المحادثة");
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('ar-SA', {
      dateStyle: 'short',
      timeStyle: 'short'
    }).format(date);
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-700 rounded-2xl flex items-center justify-center mb-4">
              <MessageCircle className="w-8 h-8 text-white" />
            </div>
            <CardTitle className="text-2xl">لوحة المحادثات</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">اسم المستخدم</label>
                <input
                  type="text"
                  value={credentials.username}
                  onChange={(e) => setCredentials({ ...credentials, username: e.target.value })}
                  className="w-full px-3 py-2 border rounded-md"
                  dir="ltr"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">كلمة المرور</label>
                <input
                  type="password"
                  value={credentials.password}
                  onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
                  className="w-full px-3 py-2 border rounded-md"
                  dir="ltr"
                />
              </div>
              <Button type="submit" className="w-full bg-gradient-to-r from-blue-500 to-blue-700 hover:from-blue-600 hover:to-blue-800">
                تسجيل الدخول
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-4 md:p-8" dir="rtl">
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-800">لوحة المحادثات</h1>
            <p className="text-gray-600 mt-1">متابعة محادثات العملاء</p>
          </div>
          <div className="flex gap-3">
            <Button onClick={loadConversations} variant="outline" disabled={loading}>
              {loading ? "جاري التحميل..." : "تحديث"}
            </Button>
            <Button onClick={handleLogout} variant="outline">
              تسجيل الخروج
            </Button>
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-3 mb-8">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
                <Users className="w-4 h-4" />
                إجمالي المحادثات
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-blue-600">{conversations.length}</div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
                <AlertCircle className="w-4 h-4" />
                تحتاج تدخل بشري
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-orange-600">
                {conversations.filter(c => c.needs_human).length}
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
                <MessageCircle className="w-4 h-4" />
                إجمالي الرسائل
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-blue-600">
                {conversations.reduce((sum, c) => sum + c.message_count, 0)}
              </div>
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>جميع المحادثات</CardTitle>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[600px]">
              <div className="space-y-3">
                {conversations.length === 0 && !loading && (
                  <div className="text-center py-12 text-gray-500">
                    لا توجد محادثات حتى الآن
                  </div>
                )}
                
                {conversations.map((conv) => (
                  <Card 
                    key={conv.customer_id} 
                    className={`cursor-pointer hover:shadow-md transition-shadow ${conv.needs_human ? 'border-orange-300 bg-orange-50' : ''}`}
                    onClick={() => viewConversation(conv)}
                  >
                    <CardContent className="p-4">
                      <div className="flex justify-between items-start mb-2">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-700 rounded-lg flex items-center justify-center">
                            <User className="w-5 h-5 text-white" />
                          </div>
                          <div>
                            <h3 className="font-bold text-gray-800">{conv.customer_name}</h3>
                            <div className="flex items-center gap-2 text-sm text-gray-600">
                              <Mail className="w-3 h-3" />
                              {conv.customer_email}
                            </div>
                          </div>
                        </div>
                        <div className="text-left">
                          <div className="flex items-center gap-1 text-xs text-gray-500 mb-1">
                            <Clock className="w-3 h-3" />
                            {formatDate(conv.last_timestamp)}
                          </div>
                          <div className="flex gap-2">
                            <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs">
                              {conv.message_count} رسالة
                            </span>
                            {conv.needs_human && (
                              <span className="px-2 py-1 bg-orange-100 text-orange-700 rounded text-xs flex items-center gap-1">
                                <AlertCircle className="w-3 h-3" />
                                تحتاج رد
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="mt-3 space-y-1">
                        <div className="text-sm text-gray-600">
                          <span className="font-medium">آخر سؤال:</span> {conv.last_message.substring(0, 80)}...
                        </div>
                        <div className="text-sm text-gray-500">
                          <span className="font-medium">الرد:</span> {conv.last_response.substring(0, 80)}...
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>

        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent className="max-w-4xl max-h-[80vh]" dir="rtl">
            <DialogHeader>
              <DialogTitle className="flex items-center justify-between">
                <div>
                  <div className="text-xl">{selectedConversation?.customer_name}</div>
                  <div className="text-sm text-gray-600 font-normal">{selectedConversation?.customer_email}</div>
                </div>
                <Button
                  size="sm"
                  onClick={() => window.open(`mailto:${selectedConversation?.customer_email}`, '_blank')}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  <Mail className="w-4 h-4 ml-2" />
                  مراسلة العميل
                </Button>
              </DialogTitle>
            </DialogHeader>
            <ScrollArea className="h-[500px] mt-4">
              <div className="space-y-4">
                {chatHistory.map((msg, idx) => (
                  <div key={idx} className="space-y-2">
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-gray-200 rounded-lg flex items-center justify-center flex-shrink-0">
                        <User className="w-4 h-4 text-gray-600" />
                      </div>
                      <div className="flex-1">
                        <div className="bg-gray-100 rounded-lg p-3">
                          <div className="text-sm text-gray-600 mb-1">العميل:</div>
                          <div className="text-gray-800">{msg.message}</div>
                        </div>
                        <div className="text-xs text-gray-500 mt-1">{formatDate(msg.timestamp)}</div>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-700 rounded-lg flex items-center justify-center flex-shrink-0">
                        <MessageCircle className="w-4 h-4 text-white" />
                      </div>
                      <div className="flex-1">
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                          <div className="text-sm text-blue-600 mb-1">المساعد الذكي:</div>
                          <div className="text-gray-800 whitespace-pre-wrap">{msg.response}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
